import React from 'react';
import { motion } from 'framer-motion';
import { PRIMARY, OUTLINE, SUCCESS, DANGER, NEUTRAL } from '../../config/designTokens';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  fullWidth?: boolean;
}
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  disabled,
  onClick,
  ...restProps
}: ButtonProps) {
  // Base styles: rounded, font-bold (friendly), flex centered
  const baseStyles = 'inline-flex items-center justify-center font-bold rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none';
  // Variants with "3D" feel (bottom border) for tactile clickiness
  const variants = {
    primary: `${PRIMARY.bg} ${PRIMARY.textOnPrimary} ${PRIMARY.shadow3d} ${PRIMARY.bgHover} ${PRIMARY.shadow3dHover} hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none focus:${PRIMARY.ring}`,
    secondary: `${NEUTRAL.bg} ${NEUTRAL.textDark} border-2 ${NEUTRAL.border} ${NEUTRAL.shadow3d} ${NEUTRAL.bgHover} hover:text-slate-900 hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none focus:${NEUTRAL.ring}`,
    success: `${SUCCESS.bg} ${SUCCESS.textDark} ${SUCCESS.shadow3d} hover:bg-emerald-400 hover:shadow-[0_4px_0_0_rgb(5,150,105)] hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none focus:${SUCCESS.ring}`,
    danger: `${DANGER.bg} text-white ${DANGER.shadow3d} ${DANGER.bgHover} hover:shadow-[0_4px_0_0_rgb(217,119,6)] hover:-translate-y-0.5 active:translate-y-[2px] active:shadow-none focus:${DANGER.ring}`,
    outline: `${OUTLINE.bg} border-2 ${OUTLINE.border} ${OUTLINE.text} ${OUTLINE.bgHover} ${OUTLINE.borderHover} ${OUTLINE.bgActive} focus:${OUTLINE.ring}`,
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 focus:ring-slate-300'
  };
  // IMPROVED: Larger sizes, better touch targets (48px minimum)
  const sizes = {
    sm: 'px-4 py-2 text-sm min-h-[44px]',
    md: 'px-6 py-3 text-base min-h-[48px]',
    lg: 'px-8 py-4 text-lg min-h-[52px]',
    xl: 'px-10 py-5 text-xl min-h-[56px]',
    xxl: 'px-12 py-6 text-2xl min-h-[64px]'
  };
  // If disabled, use a flatter, simpler style
  const variantClass = disabled ? variants[variant].replace(/shadow-\[.*?\]|hover:.*?|active:.*?/g, '') : variants[variant];
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      hapticManager.hapticLight();
    }
    if (onClick) {
      onClick(e);
    }
  };
  return <motion.button whileTap={!disabled ? {
    scale: 0.97
  } : {}} className={`
        ${baseStyles}
        ${variantClass}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `} disabled={disabled} onClick={handleClick} {...restProps} data-id="element-2477">
      {children}
    </motion.button>;
}